<script>
export default {
	name: 'DestinationWindow',
	inheritAttrs: false,
};
</script>

<script setup>
import dayjs from 'dayjs';
import VueSwiper from '~/components/VueSwiper.vue';
import CTAButton from '~/aem-components/CTAButton.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import usePersistencyWithExpiry from '~/logic/composables/usePersistencyWithExpiry.js';
import { useEventBus } from '@vueuse/core';
import { MHScrollToElement } from '~/logic/helpers/dom.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useONDList } from '~/logic/composables/booking-widget/useONDList';

dayjs.extend(isSameOrAfter);

const globalBookFlightBus = useEventBus('booking-widget:book-flight');

const props = defineProps({
	title: { type: String, default: '' },
	type: { type: String, default: 'stacked' },
	windowList: { type: Array, default: () => ([]) },
	destinationText: { type: String, default: '' },
	destinationUrl: { type: String, default: '' },
	isPersonalizationOn: { type: Boolean, default: false },
	originDescription: { type: String, default: '' },
	airportCode: { type: String, default: '' },
	airportName: { type: String, default: '' },
	bookDate: { type: Number, default: 0 },
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	showMoreText: { type: String, default: '' },
	componentIdAA: { type: String, default: '' },
});
const emit = defineEmits([
	// 'update:modelValue',
]);

const pastSearches = ref([]);

const isShowMoreClicked = ref(false);
const lockedWindowItem = ref([]);
const availableDestinationList = ref([]);

const computedBookDate = computed(() => {
	const bookDate = dayjs(props.bookDate);
	const today = dayjs();
	
	if (bookDate.isBefore(today, 'day')) {
		return dayjs(today).add(30, 'day').format('DD MMM YYYY');
	}
	
	if (bookDate.isSameOrAfter(today, 'day')) {
		return dayjs(props.bookDate).format('DD MMM YYYY');
	}
});

const toggleLockWindow = (windowItemIndex) => {
	if (lockedWindowItem.value.includes(windowItemIndex)) {
		lockedWindowItem.value.splice(lockedWindowItem.value.indexOf(windowItemIndex), 1);
	} else {
		lockedWindowItem.value.push(windowItemIndex);
	}
};

const { isEditorMode, pageProperties } = useGlobalAEMState();

const { updateFromValue, optionsTo, optionsToFlattened } = useONDList({
	fromValueRef: ref(props.airportCode),
	toValueRef: ref(null),
	listIdentifier: 'FLIGHT_SEARCH_CASH',
});

updateFromValue().then(() => {
	availableDestinationList.value = optionsToFlattened.value.map((item) => {
		return item.airportCode;
	});
});

usePersistencyWithExpiry('flight-search:past-searches', {
	refs: {
		pastSearches,
	},
});

const openFlightSearch = ((from, to) => {
	if (!document.querySelector('.BookingWidget')) {
		MHScrollToElement(document.querySelector('.FlightSearch'));
	} else {
		MHScrollToElement(document.querySelector('.BookingWidget'));
	}
	
	globalBookFlightBus.emit('active-tab-and-fill-up-form', { mainTab: 'flight-search-tab', locationFrom: from, locationTo: to });
});

const modifiedWindowList = computed(() => {
	if (!props.isPersonalizationOn) return props.windowList;
	
	const result = props.windowList.map((windowItem) => {
		let isRecommended = false;
		pastSearches.value?.forEach((pastSearch) => {
			if (pastSearch?.ondValueTo?.value) {
				if (windowItem.properties.pageProperties.destinationWindowAirportCode === pastSearch.ondValueTo.value
					&& availableDestinationList.value.includes(windowItem.properties.pageProperties.destinationWindowAirportCode)) {
					isRecommended = true;
				}
			}
		});
		return { ...windowItem, isRecommended: isRecommended };
	});
	return result.sort(function (x, y) {
		return y.isRecommended - x.isRecommended;
	});
});

</script>



<template>
<template v-if="isEditorMode && props.isHideInDesktop">
	<div class="generic-container py-10 text-center">
		<p>Hidden in: <span v-if="props.isHideInDesktop">Desktop</span> <span v-if="props.isHideInMobile">, Mobile</span></p>
	</div>
</template>

<template v-else>
	<div class="DestinationWindow">
		<div :class="{ '>md:hidden': isHideInDesktop, 'hidden >md:block': isHideInMobile }">
			<div class="text-center text-neutral-white-base">
				<h2 class="pb-4 pt-4">{{ props.title }}</h2>
				<div class="pb-10">
					<span v-html-sanitize="props.originDescription"></span>
				</div>
			</div>
			<div v-if="props.type === 'stacked'">
				<div class="stack-container pb-4">
					<div
						v-for="(windowItem, index) in modifiedWindowList"
						:key="index"
						:class="{
							'hidden': index >= 6 && !isShowMoreClicked,
							'lock-window': lockedWindowItem.includes(index) && !isMobileViewport,
						}"
						class="destination-window-wrapper group m-3"
						@click="toggleLockWindow(index)"
					>
						<div class="destination-window-content-container">
							<div class="handle-wrapper">
								<svg class="handle" style="margin: 8% auto 0;" width="39" height="2">
									<rect width="39.13" height="2.03" rx="1.02" fill="#ceaf87" />
								</svg>
							</div>
							<div
								class="destination-window-content flex flex-col pt-20 lg:pt-20 md:pt-15 items-center text-neutral-white-base"
								:style="{
									'background-image': `linear-gradient(rgba(0, 0, 0, 0.9) 10%, rgba(0, 0, 0, 0.8) 40%, rgba(0, 0, 0, 0.2) 80%, rgba(0, 0, 0, 0.9) 95%), url('${windowItem.properties.pageProperties.destinationWindowImage}')`,
								}"
								:title="windowItem.properties.pageProperties.destinationWindowImageAltText"
							>
								<span class="sr-only">{{ windowItem.properties.pageProperties.destinationWindowImageAltText }}</span>
								<p class="destination-title heading-5">{{ windowItem.properties.pageProperties.destinationWindowName }}</p>
								<p
									:class="{
										'invisible': parseInt(windowItem.lowFarePrice) <= 0,
									}"
									class="cabin-class-txt text-sm"
								>
									{{ windowItem.economyLabel }}
								</p>
								<p
									:class="{
										'invisible': parseInt(windowItem.lowFarePrice) <= 0,
									}"
									class="currency-txt text-sm"
								>
									{{ pageProperties.baseCurrency }}
								</p>
								<p
									:class="{
										'invisible': parseInt(windowItem.lowFarePrice) <= 0,
									}"
									class="price-txt heading-5"
								>
									{{ Math.round(parseFloat(windowItem.lowFarePrice)) }}
								</p>
								<p
									:class="{
										'invisible': parseInt(windowItem.lowFarePrice) <= 0,
									}"
									class="date-txt text-sm"
								>
									{{ $t('Book before') }} {{ computedBookDate }}
								</p>
								<div
									v-if="windowItem.properties.pageProperties.destinationWindowTags?.length > 0 || windowItem.isRecommended"
									class="destination-window-content-tag"
								>
									<div v-if="!windowItem?.isRecommended">
										<p
											v-for="(windowTag, index2) in windowItem.properties.pageProperties.destinationWindowTags"
											:key="index2"
										>
											{{ windowTag.title }}
										</p>
									</div>
									<div v-else>
										<p>{{ $t('Recommended For You') }}</p>
									</div>
								</div>
								<CTAButton
									v-aa="[
										{
											clickName: windowItem.bookNowText,
											clickComponentType: 'Button',
											componentName: 'DestinationWindow',
											componentID: props.componentIdAA,
											origin: props.airportCode,
											destination: windowItem.properties.pageProperties.destinationWindowAirportCode,
										},
										{
											type: 'other',
											name: windowItem.bookNowText,
										},
									]"
									containerClass="mb-3 destination-window-content-btn"
									containerStyle="--focus-visible-outline-offset: -1px;"
									sizeType="small"
									styleType="primary"
									:ctaText="windowItem.bookNowText"
									@click="openFlightSearch(props.airportCode, windowItem.properties.pageProperties.destinationWindowAirportCode)"
								></CTAButton>
								<CTAButton
									v-aa="[
										{
											clickName: windowItem.exploreText,
											clickComponentType: 'Button',
											componentName: 'DestinationWindow',
											componentID: props.componentIdAA,
											origin: props.airportCode,
											destination: windowItem.properties.pageProperties.destinationWindowAirportCode,
										},
										{
											type: 'other',
											name: windowItem.exploreText,
										},
									]"
									containerClass="destination-window-content-btn"
									containerStyle="--focus-visible-outline-offset: -2px;"
									sizeType="small"
									styleType="secondary"
									:ctaText="windowItem.exploreText"
									:href="windowItem.pageUrl"
								></CTAButton>
							</div>
						</div>
						<div class="destination-window-frame-container" role="presentation">
							<img
								src="@/assets/destination-window/destination-window.svg"
								class="destination-window-frame"
								alt=""
							/>
						</div>
					</div>
				</div>
				<CTAButton
					v-if="props.windowList.length > 6 && !isShowMoreClicked"
					containerStyle="--focus-visible-outline-offset: -2px;"
					containerClass="pt-10 pb-4"
					:ctaText="props.showMoreText"
					sizeType="small"
					styleType="secondary"
					@click="isShowMoreClicked = true"
				>
				</CTAButton>
			</div>

			<div v-if="props.type === 'carousel'">
				<div class="relative">
					<VueSwiper
						class="pb-8"
						:dormantModeEnableGridLayout="true"
						:handleTabIndex="true"
						:slidesPerView="1.1"
						:dormantMode="isEditorMode"
						direction="horizontal"
						:slidesPerGroup="1"
						:slidesPerGroupSkip="1"
						:spaceBetween="0"
						:navigation="!isMobileViewport"
						:centerInsufficientSlides="true"
						:allowTouchMove="true"
						:slidesOffsetBefore="20"
						:breakpoints="{
							'360': {
								slidesPerView: 1.1,
							},
							// '374.1': {
							// 	slidesPerView: 1.3,
							// },
							'424.1': {
								slidesPerView: 1.3,
							},
							'767.1': {
								allowTouchMove: false,
								slidesPerView: 2.2,
								slidesPerGroup: 2,
							},
							'1024': {
								allowTouchMove: false,
								slidesPerView: 3.1,
								slidesPerGroup: 2,
							},
							'1280': {
								allowTouchMove: false,
								slidesPerView: 4.05,
								slidesPerGroup: 2,
								spaceBetween: 0,
							},
						}"
					>
						<div
							v-for="(windowItem, index) in modifiedWindowList"
							:key="index"
							:class="{
								'lock-window': lockedWindowItem.includes(index) && !isMobileViewport,
							}"
							class="destination-window-wrapper m-3"
							@click="toggleLockWindow(index)"
						>
							<div class="destination-window-content-container">
								<div class="handle-wrapper">
									<svg class="handle" style="margin: 8% auto 0;" width="39" height="2">
										<rect width="39.13" height="2.03" rx="1.02" fill="#ceaf87" />
									</svg>
								</div>
								<div
									class="destination-window-content flex flex-col pt-20 lg:pt-20 md:pt-15 items-center text-neutral-white-base"
									:style="{
										'background-image': `linear-gradient(rgba(0, 0, 0, 0.9) 10%, rgba(0, 0, 0, 0.8) 20%, rgba(0, 0, 0, 0.2) 80%, rgba(0, 0, 0, 0.9) 100%), url('${windowItem.properties.pageProperties.destinationWindowImage}')`,
									}"
									:title="windowItem.properties.pageProperties.destinationWindowImageAltText"
								>
									<span class="sr-only">{{ windowItem.properties.pageProperties.destinationWindowImageAltText }}</span>
									<p class="destination-title heading-5">{{ windowItem.properties.pageProperties.destinationWindowName }}</p>
									<p
										:class="{
											'invisible': parseInt(windowItem.lowFarePrice) <= 0,
										}"
										class="cabin-class-txt text-sm"
									>
										{{ windowItem.economyLabel }}
									</p>
									<p
										:class="{
											'invisible': parseInt(windowItem.lowFarePrice) <= 0,
										}"
										class="currency-txt text-sm"
									>
										{{ pageProperties.baseCurrency }}
									</p>
									<p
										:class="{
											'invisible': parseInt(windowItem.lowFarePrice) <= 0,
										}"
										class="price-txt heading-5"
									>
										{{ Math.round(parseFloat(windowItem.lowFarePrice)) }}
									</p>
									<p
										:class="{
											'invisible': parseInt(windowItem.lowFarePrice) <= 0,
										}"
										class="date-txt text-sm"
									>
										{{ $t('Book before') }} {{ computedBookDate }}
									</p>
									<div
										v-if="windowItem.properties.pageProperties.destinationWindowTags?.length > 0 || windowItem.isRecommended"
										class="destination-window-content-tag"
									>
										<div v-if="!windowItem?.isRecommended">
											<p
												v-for="(windowTag, index2) in windowItem.properties.pageProperties.destinationWindowTags"
												:key="index2"
											>
												{{ windowTag.title }}
											</p>
										</div>
										<div v-else>
											<p>{{ $t('Recommended For You') }}</p>
										</div>
									</div>
									<CTAButton
										v-aa="[
											{
												clickName: windowItem.bookNowText,
												clickComponentType: 'Button',
												componentName: 'DestinationWindow',
												componentID: props.componentIdAA,
												origin: props.airportCode,
												destination: windowItem.properties.pageProperties.destinationWindowAirportCode,
											},
											{
												type: 'other',
												name: windowItem.bookNowText,
											},
										]"
										containerClass="mb-3 destination-window-content-btn"
										containerStyle="--focus-visible-outline-offset: -1px;"
										sizeType="small"
										styleType="primary"
										:ctaText="windowItem.bookNowText"
										@click="openFlightSearch(props.airportCode, windowItem.properties.pageProperties.destinationWindowAirportCode)"
									></CTAButton>
									<CTAButton
										v-aa="[
											{
												clickName: windowItem.exploreText,
												clickComponentType: 'Button',
												componentName: 'DestinationWindow',
												componentID: props.componentIdAA,
												origin: props.airportCode,
												destination: windowItem.properties.pageProperties.destinationWindowAirportCode,
											},
											{
												type: 'other',
												name: windowItem.exploreText,
											},
										]"
										containerClass="destination-window-content-btn"
										containerStyle="--focus-visible-outline-offset: -2px;"
										sizeType="small"
										styleType="secondary"
										:ctaText="windowItem.exploreText"
										:href="windowItem.pageUrl"
									></CTAButton>
								</div>
							</div>
							<div class="destination-window-frame-container" role="presentation">
								<img
									src="@/assets/destination-window/destination-window.svg"
									class="destination-window-frame"
									alt=""
								/>
							</div>
						</div>
					</VueSwiper>
				</div>
				<CTAButton
					containerStyle="--focus-visible-outline-offset: -2px;"
					containerClass="pb-2"
					sizeType="small"
					styleType="secondary"
					:ctaText="props.destinationText"
					:href="props.destinationUrl"
				>
				</CTAButton>
			</div>
		</div>
	</div>
</template>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.DestinationWindow {
	:deep(.destination-window-content-btn) {
		visibility: hidden;
		animation: fade-out forwards 1;
		// animation-delay: 0.1s;

		@keyframes fade-out {
			0% {
				opacity: 1;
			}
			100% {
				height: 0;
				opacity: 0;
			}
		}

		.CTAButton {
			@apply text-xs py-2 px-4;
		}
	}

	:deep(.swiper-button-prev) {
		height: 56px;
		width: 56px;
		border: 1px solid var.$neutral-grey-light;
		border-radius: 28px;
		@apply bg-primary-black-base/20;
		transition: opacity .25s ease-in-out;
		left: 0px;
		opacity: 0.5;

		html[dir="rtl"] & {
			@apply transform -rotate-180 left-auto;
		}

		&:hover {
			@apply bg-primary-black-base/60;
		}
		
		&:hover::after {
			transform: translateX(-2px);
		}

		&::after {
			content: "";
			background-image: url('~/assets/carousel/chevron-left.svg');
			background-size: cover;
			background-position: center;
			background-color: transparent;
			height: 26px;
			width: 13px;
			transition: 200ms ease-in-out;
		}
	}

	:deep(.swiper-button-next) {
		height: 56px;
		width: 56px;
		border: 1px solid var.$neutral-grey-light;
		border-radius: 28px;
		@apply bg-primary-black-base/20;
		opacity: 0.5;
		right: 0px;
		transition: 200ms ease-in-out;

		html[dir="rtl"] & {
			@apply transform -rotate-180 right-auto;
		}

		&:hover {
			@apply bg-primary-black-base/60;
		}
		
		&:hover::after {
			transform: rotate(180deg) translateX(-2px);
		}

		&::after {
			content: "";
			background-image: url('~/assets/carousel/chevron-left.svg');
			background-size: cover;
			background-position: center;
			background-color: transparent;
			height: 26px;
			width: 13px;
			transform: rotate(180deg);
			transition: 200ms ease-in-out;
		}
	}

	&:hover {
		:deep(.swiper-button-prev) {
			opacity: 1;
		}

		:deep(.swiper-button-next) {
			opacity: 1;
		}
	}
}

.stack-container {
	@apply grid gap-x-6 gap-y-10 justify-items-center;
	
	@media #{var.$query-max-md} {
		grid-template-columns: repeat(1, minmax(0, 1fr));
	}

	@media #{var.$query-min-md} {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media #{var.$query-min-lg} {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}

	@media #{var.$query-min-xl} {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}
}

.destination-window-wrapper {
	position: relative;
	width: 282px;
	height: 388px;
	opacity: 1;
	user-select: none;
	transition: filter .5s;
	filter: drop-shadow(0 3px 7px transparent);

	// @media #{var.$query-max-xs} {
	// 	width: 254px;
	// 	height: 350px;
	// }

	// @media #{var.$query-min-xs} and #{var.$query-max-md} {
	// 	width: 254px;
	// 	height: 350px;
	// }

	@media #{var.$query-max-md} {
		@apply mx-auto;
		
		width: 254px;
		height: 350px;
	}

	@media #{var.$query-min-md} and #{var.$query-max-lg} {
		width: 282px;
		height: 388px;
	}

	&:focus-visible {
		outline: none;
	}
	
	&:hover, &:focus-within, &.lock-window {
		filter: drop-shadow(0 3px 7px color.change(#000, $alpha: 0.21));
		.handle-wrapper {
			transform: translateY(86%);
			transition-duration: 0.5s;
			transition-delay: 0.1s;

			@media #{var.$query-max-xs} {
				transform: translateY(79%);
			}

			@media #{var.$query-min-md} and #{var.$query-max-lg} {
				// transform: translateY(79%);
			}
		}

		:deep(.destination-window-content-btn) {
			visibility: visible;
			animation: fade-in 0.3s forwards 1;
			// animation-delay: 0.1s;
		}

		@keyframes fade-in {
			0% {
				opacity: 0;
			}
			100% {
				opacity: 1;
			}
		}

		.destination-window-content-tag {
			animation: fade-out forwards 1;
			// animation-delay: 0.1s;
		}

		@keyframes fade-out {
			0% {
				opacity: 1;
			}
			100% {
				opacity: 0;
				height: 0;
			}
		}

		@media #{var.$query-max-md} {
			.cabin-class-txt {
				display: none;
			}

			.currency-txt {
				display: none;
			}

			.price-txt {
				display: none;
			}

			.date-txt {
				display: none;
			}
		}
	}
}

.destination-window-content-container {
	@apply inset-x-0;

	position: absolute;
	top: 1%;
	width: 282px;
	height: 400px;
	clip-path: inset(0% 1% 9% 1% round 200px);

	@media #{var.$query-max-xs} {
		clip-path: inset(0% 1% 7% 1% round 200px);
		width: 254px;
		height: 360px;
	}

	@media #{var.$query-min-xs} and #{var.$query-max-md} {
		clip-path: inset(0% 1% 8% 1% round 200px);
		width: 254px;
		height: 359px;
	}
	
	@media #{var.$query-min-md} and #{var.$query-max-lg} {
		clip-path: inset(0% 1% 6% 1% round 200px);
		width: 282px;
		height: 400px;
	}
}

.destination-window-content {
	@apply mx-auto px-8;

	background-size: cover;
	background-repeat: no-repeat;
	width: 250px;
	height: 360px;
	position: relative;
	text-align: center;

	// @media #{var.$query-max-xs} {
	// 	width: 254px;
	// 	height: 350px;
	// }

	// @media #{var.$query-min-xs} and #{var.$query-max-md} {
	// 	width: 254px;
	// 	height: 349px;
	// }

	@media #{var.$query-max-md} {
		width: 254px;
		height: 350px;
	}

	@media #{var.$query-min-md} and #{var.$query-max-lg} {
		width: 250px;
		height: 360px;
	}
}

.destination-window-frame-container {
	pointer-events: none;
	position: relative;
	z-index: 100;
}

.destination-window-frame {
	max-width: 282px;

	// @media #{var.$query-max-xs} {
	// 	width: 254px;
	// }

	// @media #{var.$query-min-xs} and #{var.$query-max-md} {
	// 	width: 254px;
	// }

	@media #{var.$query-max-md} {
		width: 254px;
	}

	@media #{var.$query-min-md} and #{var.$query-max-lg} {
		max-width: 282px;
	}
}

.handle-wrapper {
	@apply mx-auto inset-x-0;

	backdrop-filter: blur(6px);
	-webkit-backdrop-filter: blur(6px);
	background-color: hsl(255, 100%, 100%, 0.2);
	position: absolute;
	z-index: 10;
	top: -76%;
	border-radius: 200px;
	width: 252px;
	height: 360px;
	transform: translateY(0);
	transition-duration: 0.5s;
	transition-delay: 0.1s;
	backface-visibility: hidden;

	@media #{var.$query-max-xs} {
		top: -83%;
		width: 254px;
		height: 350px;
	}

	@media #{var.$query-min-xs} and #{var.$query-max-md} {
		top: -75%;
		width: 234px;
		height: 319px;
	}

	@media #{var.$query-min-md} and #{var.$query-max-lg} {
		width: 252px;
		height: 360px;
	}
}

.handle {
	position: absolute;
	left: 0;
	right: 0;
	top: 88%;
}

.destination-window-content-tag {
	@apply bg-neutral-white-base text-primary-blue-base rounded px-2 text-xs font-bold py-0.5;
}

.destination-window-content :not(.destination-window-content-tag) {
  z-index: 20;
}

.destination-title {
	@apply mb-3 line-clamp-2;

	@media #{var.$query-max-md} {
		@apply text-xl mb-4;
	}
}

.cabin-class-txt {
	@apply line-clamp-1;

	@media #{var.$query-max-md} {
		@apply text-sm;
	}
}

.currency-txt {
	@apply font-semibold mb-0;

	@media #{var.$query-max-md} {
		@apply text-sm;
	}
}

.price-txt {
	@apply mb-2 line-clamp-1;

	@media #{var.$query-max-md} {
		@apply mb-3 text-xl;
	}
}

.date-txt {
	@apply mb-4 line-clamp-1;

	@media #{var.$query-max-md} {
		@apply text-sm;
	}
}

.swiper-arrow {
	@apply h-14 w-14 inline-flex items-center justify-center p-0 rounded-full;

	background-color: var(--primary-black-base);
	opacity: 20%;
	border: solid 1.5px var(--neutral-grey-light);
	.arrow-icon-right, .arrow-icon-left {
		@apply h-8 w-8 text-lg fill-neutral-white-base;

		transition: transform .3s ease-in-out;
	}
	
	&:hover {
		opacity: 60%;
		.arrow-icon-right {
			transform: translateX(2px);
		}
		.arrow-icon-left {
			transform: translateX(-2px);
		}
	}

	&:active {
		opacity: 100%;
	}
}

.swiper-arrow-wrapper {
	@apply z-10 flex items-center flex-shrink-0 absolute inset-y-0;

	@media #{var.$query-max-md} {
		display: none;
	}
}

.swiper-button-disabled {
	display: none;
}
</style>
